import { Heading, Icon } from "@afa-shared/afa-components";
import { ContentLink } from "@components/Link/ContentLink";
import { StandardLink } from "@components/Link/StandardLink";
import clsx from "clsx";
import { RefObject } from "react";

import { Context } from "types/context/Context";

import contactCardStyles from "./ContactCard.module.css";

interface IContactCard {
  imageUrl?: string;
  imageAlt?: string;
  personalName?: string;
  headingVariant?: "h2" | "h3" | "h4" | "h5" | "h6";
  email?: string;
  emailTitleTag?: string;
  emailOnClick?: (value?: any) => void;
  phone?: string;
  phoneLabel?: string;
  phoneTitleTag?: string;
  phoneOnClick?: (value?: any) => void;
  phoneTwo?: string;
  phoneTwoLabel?: string;
  phoneTwoTitleTag?: string;
  phoneTwoOnClick?: (value?: any) => void;
  addressType?: boolean;
  address?: string;
  addressLink?: string;
  addressRef?: RefObject<HTMLAnchorElement | null>;
  box?: string;
  zipCode?: string;
  faxLabel?: string;
  fax?: string;
  faxTitleTag?: string;
  state?: string;
  jobTitle?: string;
  extraInfo?: string;
  biography?: any;
  variant?: "link" | "button";
  linkText?: string;
  linkType?: "open_in_new" | "arrow_forward" | "file_download";
  linkUrl?: string;
  linkRef?: RefObject<HTMLAnchorElement | null>;
  removeBorder?: boolean;
  dataTestIdPhone?: string;
  dataTestIdPhoneTwo?: string;
  dataTestIdEmail?: string;
  className?: string;
  context?: Context;
}

export enum LinkType {
  External = "open_in_new",
  Internal = "arrow_forward",
  File = "file_download",
}

const ContactCard = ({
  personalName,
  headingVariant = "h2",
  email,
  emailTitleTag,
  phone,
  phoneLabel,
  phoneTitleTag,
  phoneOnClick,
  phoneTwo,
  phoneTwoLabel,
  phoneTwoTitleTag,
  phoneTwoOnClick,
  jobTitle,
  extraInfo,
  imageUrl,
  imageAlt,
  biography,
  address,
  addressType,
  addressLink,
  addressRef,
  box,
  zipCode,
  faxLabel,
  fax,
  faxTitleTag,
  state,
  variant,
  linkText,
  linkType,
  linkUrl,
  linkRef,
  removeBorder,
  dataTestIdPhone,
  dataTestIdPhoneTwo,
  dataTestIdEmail,
  className,
  context,
}: IContactCard): React.ReactElement => {
  const classes = className ?? "";
  const formattedPhoneLabel =
    phoneLabel && (phoneLabel?.endsWith(" ") ? phoneLabel : phoneLabel + " ");
  const formattedPhoneTwoLabel =
    phoneTwoLabel && (phoneTwoLabel?.endsWith(" ") ? phoneTwoLabel : phoneTwoLabel + " ");
  const formattedFaxLabel = faxLabel && (faxLabel?.endsWith(" ") ? faxLabel : faxLabel + " ");

  const getAddress = () => (
    <span className={contactCardStyles.addressWrapper}>
      {addressType ? (
        <Icon name="mail_square" referenceSize="span" />
      ) : (
        <Icon name="map_square" referenceSize="span" />
      )}
      <div className={contactCardStyles.address}>
        {address && <span>{address}</span>}
        {box && <span>{box}</span>}
        {(zipCode || state) && (
          <span>
            {zipCode} {state}
          </span>
        )}
      </div>
    </span>
  );

  return (
    <div
      className={clsx(contactCardStyles.contactCard, classes, {
        [contactCardStyles.noBorder]: removeBorder,
      })}
    >
      {imageUrl && (
        <img className={contactCardStyles.image} src={imageUrl} alt={imageAlt} loading="lazy" />
      )}
      <div className={contactCardStyles.contactWrapper}>
        <div className={contactCardStyles.infoWrapper}>
          {personalName && (
            <Heading
              variant={headingVariant}
              className={`${contactCardStyles.heading} ${imageUrl ? contactCardStyles.hasImage : ""}`}
            >
              {personalName}
            </Heading>
          )}
          {jobTitle && <span>{jobTitle}</span>}
          {extraInfo && <span>{extraInfo}</span>}
          {(email || phone || phoneTwo || address || fax) && (
            <address className={contactCardStyles.contactInfo}>
              {email && (
                <ContentLink
                  href={`mailto:${email}`}
                  aria-label={emailTitleTag}
                  // onClick={emailOnClick}
                  data-testid={dataTestIdEmail}
                  contentText={email}
                  isExternal={true}
                  trackingData={{
                    sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
                    linkType: "contact",
                  }}
                >
                  <Icon name="email" referenceSize="span" />
                  <span>{email}</span>
                </ContentLink>
              )}
              {phone && (
                <ContentLink
                  href={`tel:${phone}`}
                  aria-label={phoneTitleTag}
                  onClick={phoneOnClick}
                  data-testid={dataTestIdPhone}
                  contentText={formattedPhoneLabel + phone}
                  isExternal={true}
                  trackingData={{
                    sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
                    linkType: "contact",
                  }}
                >
                  <Icon name="phone_square" referenceSize="span" />
                  <span>
                    {formattedPhoneLabel}
                    {phone}
                  </span>
                </ContentLink>
              )}
              {phoneTwo && (
                <ContentLink
                  href={`tel:${phoneTwo}`}
                  aria-label={phoneTwoTitleTag}
                  onClick={phoneTwoOnClick}
                  data-testid={dataTestIdPhoneTwo}
                  contentText={formattedPhoneTwoLabel + phoneTwo}
                  isExternal={true}
                  trackingData={{
                    sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
                    linkType: "contact",
                  }}
                >
                  <Icon name="phone_square" referenceSize="span" />
                  <span>
                    {formattedPhoneTwoLabel}
                    {phoneTwo}
                  </span>
                </ContentLink>
              )}
              {(address || box || zipCode || state) &&
                (addressLink ? (
                  <ContentLink
                    href={addressLink}
                    ref={addressRef}
                    isExternal={true}
                    contentText={addressLink}
                    trackingData={{ linkType: "contact" }}
                  >
                    {getAddress()}
                  </ContentLink>
                ) : (
                  <>{getAddress()}</>
                ))}
              {fax && (
                <ContentLink
                  href={`fax:${fax}`}
                  aria-label={faxTitleTag}
                  isExternal={true}
                  contentText={formattedFaxLabel + fax}
                  trackingData={{
                    sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
                    sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
                    linkType: "contact",
                  }}
                >
                  <Icon name="fax_square" referenceSize="span" />
                  <span>
                    {formattedFaxLabel}
                    {fax}
                  </span>
                </ContentLink>
              )}
            </address>
          )}
        </div>

        {biography && <div className={contactCardStyles.Biography}>{biography}</div>}
        {linkText && linkUrl && (
          <div className={contactCardStyles.linkWrapper}>
            <StandardLink
              href={linkUrl}
              text={linkText}
              variant={variant}
              type={linkType}
              hideIcon={variant === "button"}
              ref={linkRef as RefObject<HTMLAnchorElement>}
              trackingData={{
                linkType: "contact",
                sectionTitle: context?.sectionBlock?.sectionBlockHeading ?? undefined,
                sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactCard;
